package com.example.borshi.web.products

import androidx.compose.runtime.*
import com.example.borshi.web.cart.CartStore
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.home.ProductCard
import com.example.borshi.web.model.Category
import com.example.borshi.web.model.Product
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Suppress("UNUSED_PARAMETER")
@Composable
fun ProductsScreen(initialCategory: String = "All", cartStore: CartStore? = null, onAddToCart: (Product) -> Unit) {
    var searchQuery by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf(initialCategory) }  // Remove key dependency
    var selectedTagFilter by remember { mutableStateOf("") }
    var products by remember { mutableStateOf<List<Product>>(emptyList()) }
    var categories by remember { mutableStateOf<List<Category>>(emptyList()) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var dataLoaded by remember { mutableStateOf(false) }  // Track if initial load is done

    // Update selectedCategory when initialCategory changes (navigation)
    LaunchedEffect(initialCategory) {
        selectedCategory = initialCategory
    }

    // Load products and categories once
    LaunchedEffect(Unit) {
        if (!dataLoaded) {  // Only load once
            isLoading = true
            try {
                products = FirebaseService.getProducts()
                categories = FirebaseService.getCategories()
                dataLoaded = true
                isLoading = false
            } catch (e: Throwable) {
                if (e.message?.contains("left the composition") == true) return@LaunchedEffect
                try {
                    val c: dynamic = js("console")
                    c?.error?.call(c, "Firebase products load failed:", e)
                } catch (ignored: dynamic) {}
                loadError = "Failed to load products from Firebase: ${e.message ?: e.toString()}"
                products = listOf(
                    Product("fallback_1", "Fresh Salmon", 599.0, FirebaseService.getStorageUrl("images/sea_fish.png"), "Fish"),
                    Product("fallback_2", "Tiger Prawns", 899.0, FirebaseService.getStorageUrl("images/shrimp.jpg"), "Prawns"),
                    Product("fallback_3", "Pomfret Fish", 450.0, FirebaseService.getStorageUrl("images/sea_fish.png"), "Fish"),
                    Product("fallback_4", "Crab Meat", 750.0, FirebaseService.getStorageUrl("images/ic_shellfish.jpg"), "Shellfish"),
                    Product("fallback_5", "Dried Bombay Duck", 350.0, FirebaseService.getStorageUrl("images/ic_river_fish.jpg"), "Dried"),
                    Product("fallback_6", "Lobster", 1200.0, FirebaseService.getStorageUrl("images/ic_shellfish.jpg"), "Shellfish"),
                    Product("fallback_7", "Rohu Fish", 300.0, FirebaseService.getStorageUrl("images/river_fish.png"), "Fish")
                )
                dataLoaded = true
                isLoading = false
            }
        }
    }

    // Filter products by category AND (search query OR tag filter) - INDEPENDENT
    val filteredProducts = remember(products, selectedCategory, searchQuery, selectedTagFilter) {
        products.filter { product ->
            // Category filter (independent)
            val matchesCategory = selectedCategory == "All" || product.category == selectedCategory

            // Search query filter (manual text search)
            val lowerQuery = searchQuery.lowercase()
            val matchesSearch = if (searchQuery.isBlank()) {
                true
            } else {
                product.name.lowercase().contains(lowerQuery) ||
                product.category.lowercase().contains(lowerQuery) ||
                product.tags.vitamins.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.minerals.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.omega3.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.benefit.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.waterType.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.boneType.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.cutType.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.cookStyle.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.diet.any { it.lowercase().contains(lowerQuery) } ||
                product.tags.allergen.any { it.lowercase().contains(lowerQuery) }
            }

            // Tag filter (from tag clicks - independent)
            val lowerTagFilter = selectedTagFilter.lowercase()
            val matchesTagFilter = if (selectedTagFilter.isBlank()) {
                true
            } else {
                product.name.lowercase().contains(lowerTagFilter) ||
                product.tags.vitamins.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.minerals.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.omega3.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.benefit.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.waterType.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.boneType.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.cutType.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.cookStyle.any { it.lowercase().contains(lowerTagFilter) } ||
                product.tags.diet.any { it.lowercase().contains(lowerTagFilter) }
            }

            // All filters must match (category AND (search OR tagFilter))
            matchesCategory && matchesSearch && matchesTagFilter
        }
    }

    InjectProductStyles()

    Div({ classes("ps-root") }) {

        // Page header
        Div({ classes("ps-header") }) {
            Div({ classes("ps-eyebrow") }) { Text("Bangalore's Freshest") }
            Div({ classes("ps-title") }) { Text("Shop Seafood") }
        }

        // Search and Filter Bar
        SearchAndFilterBar(
            searchQuery = searchQuery,
            selectedCategory = selectedCategory,
            selectedTagFilter = selectedTagFilter,
            categories = categories,
            onSearchChange = { query -> searchQuery = query },
            onCategoryChange = { category ->
                selectedCategory = category
                selectedTagFilter = ""
            },
            onTagFilterChange = { tag -> selectedTagFilter = tag }
        )

        // Loading skeleton
        if (isLoading) {
            Div({ classes("ps-skeleton-grid") }) {
                repeat(8) {
                    Div({ classes("ps-skeleton-card") }) {}
                }
            }
        } else if (loadError != null) {
            Div({ classes("ps-error") }) {
                Text("Unable to load products. Showing cached data.")
                FilteredProductsGrid(onAddToCart, filteredProducts, cartStore)
            }
        } else {
            val count = filteredProducts.size
            if (count > 0) {
                Div({ classes("ps-result-bar") }) {
                    Span({ classes("ps-result-count") }) {
                        Text("$count product${if (count != 1) "s" else ""}")
                    }
                    if (selectedTagFilter.isNotBlank()) {
                        Span({
                            classes("ps-active-filter")
                            attr("title", "Clear filter")
                            attr("role", "button")
                            attr("tabindex", "0")
                        }) {
                            Text("Filter: ${formatTagDisplay(selectedTagFilter, "")}  ×")
                        }
                    }
                }
            }
            FilteredProductsGrid(onAddToCart, filteredProducts, cartStore)
        }
    }
}

@Composable
private fun InjectProductStyles() {
    DisposableEffect(Unit) {
        if (kotlinx.browser.document.getElementById("ps-styles") != null) return@DisposableEffect onDispose { }
        val s = kotlinx.browser.document.createElement("style")
        s.id = "ps-styles"
        s.textContent = """
.ps-root{max-width:1200px;margin:0 auto;padding:24px 16px 80px;width:100%;background:#F5F9F8;min-height:calc(100vh - 64px)}
.ps-header{margin-bottom:24px}
.ps-eyebrow{font-family:'Syne',sans-serif;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#26A69A;margin-bottom:4px}
.ps-title{font-family:'Syne',sans-serif;font-size:28px;font-weight:800;color:#0D2B2E;letter-spacing:-.04em;line-height:1.1}
.ps-filter{background:white;border-radius:18px;border:1.5px solid #E0EDEB;padding:20px;margin-bottom:24px;box-shadow:0 2px 16px rgba(13,43,46,0.06)}
.ps-search-wrap{position:relative;margin-bottom:18px}
.ps-search-icon{position:absolute;left:14px;top:50%;transform:translateY(-50%);font-size:16px;pointer-events:none}
.ps-search{width:100%;padding:12px 14px 12px 42px;font-size:15px;font-family:'DM Sans',sans-serif;border:1.5px solid #E0EDEB;border-radius:12px;outline:none;background:#F5F9F8;color:#0D2B2E;transition:border-color .18s ease,box-shadow .18s ease}
.ps-search:focus{border-color:#26A69A;box-shadow:0 0 0 3px rgba(38,166,154,0.12);background:white}
.ps-search::placeholder{color:#7A9598}
.ps-section-label{font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#7A9598;margin-bottom:10px;font-family:'DM Sans',sans-serif}
.ps-cat-row{display:flex;gap:8px;flex-wrap:wrap;padding-bottom:16px;border-bottom:1px solid #F0F5F4;margin-bottom:16px}
.ps-cat-pill{font-family:'DM Sans',sans-serif;border:none;padding:7px 16px;border-radius:999px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;transition:all .18s ease;background:#F5F9F8;color:#3D5A5E;border:1.5px solid transparent}
.ps-cat-pill:hover{background:#E0F2F1;color:#0D2B2E}
.ps-cat-pill.ps-selected{background:#0D2B2E;color:white;border-color:#0D2B2E}
.ps-tag-tabs{display:flex;gap:6px;overflow-x:auto;padding-bottom:10px;margin-bottom:10px;scrollbar-width:none}
.ps-tag-tabs::-webkit-scrollbar{display:none}
.ps-tag-tab{font-family:'DM Sans',sans-serif;border:1.5px solid #E0EDEB;background:white;padding:5px 12px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;color:#3D5A5E;transition:all .18s ease;flex-shrink:0}
.ps-tag-tab:hover{border-color:#26A69A;color:#0D2B2E}
.ps-tag-tab.ps-selected{background:#E0F2F1;border-color:#26A69A;color:#1A7A72}
.ps-tags{display:flex;gap:6px;flex-wrap:wrap}
.ps-tag{font-family:'DM Sans',sans-serif;border:1.5px solid #E0EDEB;background:white;padding:5px 12px;border-radius:8px;font-size:12px;font-weight:500;cursor:pointer;white-space:nowrap;color:#3D5A5E;transition:all .16s ease}
.ps-tag:hover{border-color:#C4DBD8;background:#F0FAFA}
.ps-tag.ps-tag-on{background:#0D2B2E;border-color:#0D2B2E;color:white;font-weight:600}
.ps-result-bar{display:flex;align-items:center;gap:10px;margin-bottom:16px}
.ps-result-count{font-size:12px;font-weight:600;color:#7A9598;font-family:'DM Sans',sans-serif}
.ps-active-filter{font-size:11px;font-weight:700;color:#26A69A;background:#E0F2F1;padding:3px 10px;border-radius:999px;cursor:pointer}
.ps-skeleton-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:20px;margin-top:8px}
.ps-skeleton-card{height:320px;background:linear-gradient(90deg,#E4EFEE 25%,#F2F9F8 50%,#E4EFEE 75%);background-size:700px 100%;animation:psSkelShimmer 1.5s ease-in-out infinite;border-radius:20px}
@keyframes psSkelShimmer{0%{background-position:-700px 0}100%{background-position:700px 0}}
.ps-error{font-size:13px;color:#7A9598;padding:8px 0 16px;font-family:'DM Sans',sans-serif}
.ps-empty{text-align:center;padding:60px 20px;color:#7A9598;font-family:'DM Sans',sans-serif;font-size:14px}
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(s)
        onDispose { }
    }
}

@Composable
fun FilteredProductsGrid(
    onAddToCart: (Product) -> Unit,
    products: List<Product>,
    cartStore: CartStore? = null
) {
    if (products.isEmpty()) {
        Div({ classes("ps-empty") }) {
            Text("No products match your filters. Try adjusting your search or category.")
        }
    } else {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(248px, 1fr))")
                gap(20.px)
                marginTop(4.px)
            }
        }) {
            products.forEach { product ->
                key(product.id) {
                    ProductCard(product, onAddToCart)
                }
            }
        }
    }
}

@Composable
fun CategoryPill(label: String, isSelected: Boolean, onClick: () -> Unit) {
    Button({
        if (isSelected) classes("ps-cat-pill", "ps-selected") else classes("ps-cat-pill")
        onClick { onClick() }
    }) { Text(label) }
}

@Composable
fun SearchAndFilterBar(
    searchQuery: String,
    selectedCategory: String,
    selectedTagFilter: String,
    categories: List<Category>,
    onSearchChange: (String) -> Unit,
    onCategoryChange: (String) -> Unit,
    onTagFilterChange: (String) -> Unit
) {
    var localSearchQuery by remember { mutableStateOf(searchQuery) }

    LaunchedEffect(searchQuery) {
        if (searchQuery.isBlank()) localSearchQuery = ""
    }

    Div({ classes("ps-filter") }) {
        // Search input
        Div({ classes("ps-search-wrap") }) {
            Span({ classes("ps-search-icon") }) { Text("🔍") }
            Input(type = InputType.Text) {
                classes("ps-search")
                value(localSearchQuery)
                onInput { event ->
                    localSearchQuery = event.value
                    onSearchChange(event.value)
                }
                attr("placeholder", "Search by name, cut, vitamins, omega-3...")
            }
        }

        // Categories
        Div({ classes("ps-section-label") }) { Text("Category") }
        Div({ classes("ps-cat-row") }) {
            CategoryPill("All", selectedCategory == "All") { onCategoryChange("All") }
            categories.forEach { cat ->
                CategoryPill(cat.name, selectedCategory == cat.name) { onCategoryChange(cat.name) }
            }
        }

        // Tag search
        SearchTags(selectedTagFilter = selectedTagFilter, onTagClick = onTagFilterChange)
    }
}

@Composable
fun SearchTags(selectedTagFilter: String, onTagClick: (String) -> Unit) {
    val tagCategories = remember {
        mapOf(
            "Popular"    to listOf("rohu", "katla", "hilsa", "pomfret", "prawns", "crab"),
            "Water Type" to listOf("freshwater", "seawater", "brackish"),
            "Bone Type"  to listOf("boneless", "single_bone", "many_small_bones", "shellfish"),
            "Cut Type"   to listOf("curry_cut", "steak", "fillet_boneless", "cleaned_whole", "keema"),
            "Cooking"    to listOf("fry", "curry", "grill", "steam", "bake"),
            "Benefits"   to listOf("high_omega3", "high_protein", "high_calcium", "lean", "kids_friendly"),
            "Diet"       to listOf("keto_friendly", "halal_possible", "paleo_friendly"),
            "Omega-3"    to listOf("very_high_>1500mg", "high_1000-1500mg", "medium_500-1000mg", "low_<500mg"),
            "Minerals"   to listOf("iron", "calcium", "zinc", "phosphorus", "selenium"),
            "Vitamins"   to listOf("vitamin_a", "vitamin_d", "vitamin_b12", "vitamin_e")
        )
    }
    var activeTab by remember { mutableStateOf("Popular") }

    Div({ classes("ps-section-label") }) { Text("Quick Filters") }

    // Tab row
    Div({ classes("ps-tag-tabs") }) {
        tagCategories.keys.forEach { cat ->
            Button({
                if (activeTab == cat) classes("ps-tag-tab", "ps-selected") else classes("ps-tag-tab")
                onClick { activeTab = cat }
            }) { Text(cat) }
        }
    }

    // Tag chips
    Div({ classes("ps-tags") }) {
        (tagCategories[activeTab] ?: emptyList()).forEach { tag ->
            val display = formatTagDisplay(tag, activeTab)
            val isOn = selectedTagFilter.equals(tag, ignoreCase = true)
            Button({
                if (isOn) classes("ps-tag", "ps-tag-on") else classes("ps-tag")
                onClick { onTagClick(if (isOn) "" else tag) }
            }) { Text(if (isOn) "✓ $display" else display) }
        }
    }
}

@Composable
fun TagCategoryChip(label: String, isSelected: Boolean, onClick: () -> Unit) {
    // Legacy shim
    Button({
        if (isSelected) classes("ps-tag-tab", "ps-selected") else classes("ps-tag-tab")
        onClick { onClick() }
    }) { Text(label) }
}

fun formatTagDisplay(tag: String, category: String): String {
    if (category == "Omega-3") {
        return when (tag) {
            "very_high_>1500mg"  -> "Very High >1500mg"
            "high_1000-1500mg"   -> "High 1000-1500mg"
            "medium_500-1000mg"  -> "Medium 500-1000mg"
            "low_<500mg"         -> "Low <500mg"
            else -> tag
        }
    }
    return tag.replace("_", " ").split(" ").joinToString(" ") {
        it.replaceFirstChar { c -> if (c.isLowerCase()) c.uppercase() else c.toString() }
    }
}

@Composable
fun TagChip(label: String, isSelected: Boolean = false, onClick: () -> Unit) {
    // Legacy shim
    Button({
        if (isSelected) classes("ps-tag", "ps-tag-on") else classes("ps-tag")
        onClick { onClick() }
    }) { Text(if (isSelected) "✓ $label" else label) }
}
